import React, { useState, useEffect, useCallback } from 'react';
import { X, QrCode, Sparkles, Shield, Wallet, Copy, Check, RefreshCw, Trophy, Flame, Star, Send } from 'lucide-react';
import { useToast } from '../shared/ToastNotification';
import { getAPIRoot } from '../../services/api/core';

export default function StagsterPassportModal({ isOpen, onClose }) {
  const { showSuccess, showError, showInfo } = useToast();
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('passport'); // 'passport' | 'memories' | 'preferences'
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [passportData, setPassportData] = useState(null);
  const [feedback, setFeedback] = useState({
    musicRating: 5,
    entrySpeed: 'fast',
    safetyRating: 'high',
    serviceRating: 5,
  });

  const loadPassport = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setLoadError('');
    try {
      const res = await fetch(`${getAPIRoot()}/passport/me`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        setLoadError(data?.error || 'Failed to load your passport.');
        return;
      }
      setPassportData(data.passport);
    } catch (e) {
      setLoadError('Failed to load your passport.');
    } finally {
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      loadPassport();
      setCopied(false);
    }
  }, [isOpen, loadPassport]);

  const handleCopyCode = () => {
    if (!passportData?.restId) return;
    navigator.clipboard.writeText(passportData.restId);
    setCopied(true);
    showSuccess('Passport code copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRotateCode = async () => {
    if (!passportData) return;
    try {
      const res = await fetch(`${getAPIRoot()}/passport/code/rotate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        showError(data?.error || 'Failed to rotate your code.');
        return;
      }
      setPassportData((prev) => ({ ...prev, restId: data.newCode }));
      showInfo('6-Digit STAGSTER Code rotated!');
    } catch (e) {
      showError('Failed to rotate your code.');
    }
  };

  const submitFeedback = async () => {
    const latestMemory = passportData?.nightlifeMemories?.[0];
    if (!latestMemory?.venueId) {
      showError('No check-in found to rate yet.');
      return;
    }
    try {
      const res = await fetch(`${getAPIRoot()}/passport/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          venue_id: latestMemory.venueId,
          music_rating: feedback.musicRating,
          entry_speed: feedback.entrySpeed,
          safety_rating: feedback.safetyRating,
          service_rating: feedback.serviceRating,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        showError(data?.error || 'Failed to save feedback.');
        return;
      }
      showSuccess('Private feedback saved!');
      setShowFeedbackModal(false);
    } catch (e) {
      showError('Failed to save feedback.');
    }
  };

  if (!isOpen) return null;

  const memories = passportData?.nightlifeMemories || [];
  const prefs = passportData?.preferences || {};
  const tierProgress = passportData?.nextTierProgress || {};
  const knownFaceVenues = passportData?.knownFaceVenues || [];

  return (
    <div onClick={onClose} className="fixed inset-0 z-[100] flex overflow-y-auto p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
      <div onClick={(e) => e.stopPropagation()} className="m-auto w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto bg-slate-900 text-white rounded-3xl p-5 border border-slate-800 space-y-5 shadow-2xl relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition">
          <X className="w-5 h-5" />
        </button>

        {/* Header Tabs */}
        <div className="flex items-center gap-2 border-b border-slate-800 pb-3 pr-8">
          <button
            onClick={() => setActiveTab('passport')}
            className={`px-3 py-1.5 rounded-xl font-bold text-xs transition flex items-center gap-1.5 ${
              activeTab === 'passport' ? 'bg-purple-600 text-white shadow-md' : 'text-slate-400 hover:text-white'
            }`}
          >
            <QrCode className="w-4 h-4" /> Passport
          </button>
          <button
            onClick={() => setActiveTab('memories')}
            className={`px-3 py-1.5 rounded-xl font-bold text-xs transition flex items-center gap-1.5 ${
              activeTab === 'memories' ? 'bg-purple-600 text-white shadow-md' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Sparkles className="w-4 h-4 text-amber-400" /> Memories & Diary
          </button>
          <button
            onClick={() => setActiveTab('preferences')}
            className={`px-3.5 py-1.5 rounded-xl font-bold text-xs transition flex items-center gap-1.5 ${
              activeTab === 'preferences' ? 'bg-purple-600 text-white shadow-md' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Shield className="w-4 h-4 text-emerald-400" /> Prefs
          </button>
        </div>

        {loading && (
          <div className="py-10 text-center text-sm text-slate-400">Loading your passport...</div>
        )}

        {!loading && loadError && (
          <div className="py-10 text-center text-sm text-rose-400">{loadError}</div>
        )}

        {!loading && !loadError && passportData && (
          <>
            {/* TAB 1: PASSPORT CARD */}
            {activeTab === 'passport' && (
              <div className="space-y-4">
                <div className="rounded-2xl p-5 bg-gradient-to-br from-purple-900 via-slate-900 to-indigo-950 border border-purple-500/40 text-left space-y-4 shadow-xl relative overflow-hidden">
                  <div className="flex justify-between items-center border-b border-purple-800/60 pb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 bg-amber-400 rounded-full animate-pulse"></span>
                      <span className="font-extrabold text-lg tracking-tight text-white">STAGSTER</span>
                      <span className="text-[10px] text-amber-400 font-bold bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/30">NIGHTLIFE PASSPORT</span>
                    </div>
                    <Trophy className="w-4 h-4 text-amber-400" />
                  </div>

                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-[10px] uppercase text-purple-300 font-bold tracking-wider">Member Identity</div>
                      <div className="text-base font-extrabold text-white">{passportData.fullName || 'STAGSTER Member'}</div>
                      <div className="text-xs text-slate-300 font-mono">{passportData.username ? `@${passportData.username}` : ''}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase text-emerald-400 font-bold tracking-wider">Tier Status</div>
                      <div className="text-xs font-bold text-emerald-300">{passportData.vipStatus}</div>
                    </div>
                  </div>

                  {/* Tier Progress Bar */}
                  <div className="bg-slate-950/80 p-2.5 rounded-xl border border-purple-900/40 space-y-1 text-xs">
                    <div className="flex justify-between text-[11px]">
                      <span className="text-slate-300 font-semibold">
                        Tier Progress ({passportData.visitStreak} {passportData.visitStreak === 1 ? 'visit' : 'visits'})
                      </span>
                      {tierProgress.nextTier ? (
                        <span className="text-amber-400 font-bold">{tierProgress.neededForNext} more to {tierProgress.nextTier}</span>
                      ) : (
                        <span className="text-amber-400 font-bold">Top tier reached</span>
                      )}
                    </div>
                    <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-amber-400 rounded-full" style={{ width: `${Math.max(4, Math.min(100, tierProgress.percent ?? 0))}%` }}></div>
                    </div>
                  </div>

                  {/* Code Box */}
                  <div className="bg-slate-950/80 p-3 rounded-xl border border-purple-900/60 flex items-center justify-between">
                    <div>
                      <div className="text-[9px] uppercase text-slate-400 font-bold tracking-wider">FastLane 6-Digit Code</div>
                      <div className="text-2xl font-black font-mono tracking-widest text-amber-300">#{passportData.restId}</div>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={handleCopyCode} className="p-2 rounded-lg bg-purple-600/30 hover:bg-purple-600/50 text-purple-200 border border-purple-500/40 text-xs font-bold transition flex items-center gap-1">
                        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                      <button onClick={handleRotateCode} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs transition" title="Rotate 6-digit code">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Preference Summary */}
                  <div className="flex items-center justify-between text-[11px] bg-purple-950/40 p-2.5 rounded-lg border border-purple-800/40">
                    <span className="text-slate-300 flex items-center gap-1.5">
                      <Flame className="w-3.5 h-3.5 text-amber-400" /> {prefs.vibe || 'No vibe set yet'}
                    </span>
                    {prefs.city && <span className="text-emerald-400 font-bold">📍 {prefs.city}</span>}
                  </div>
                </div>

                {/* Wallet Export Actions */}
                <div className="space-y-2 pt-1">
                  <button
                    onClick={() => showInfo('Apple Wallet export is coming soon.')}
                    className="w-full py-3 rounded-xl bg-black hover:bg-slate-950 text-white font-bold text-xs border border-slate-800 shadow-md flex items-center justify-center gap-2 transition"
                  >
                    <Wallet className="w-4 h-4" /> Add to Apple Wallet (.pkpass)
                  </button>
                  <button
                    onClick={() => showInfo('Google Wallet export is coming soon.')}
                    className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md flex items-center justify-center gap-2 transition"
                  >
                    <Wallet className="w-4 h-4" /> Save to Google Wallet
                  </button>
                </div>

                {knownFaceVenues.length > 0 && (
                  <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                      <span className="text-xs font-black uppercase tracking-wider text-emerald-300">Known Face</span>
                    </div>
                    <p className="text-[11px] text-emerald-100/70 leading-relaxed">
                      These venues know you as a regular. Walk in, show your code, no request needed.
                    </p>
                    {knownFaceVenues.map((v) => (
                      <div key={v.venue_id} className="flex items-center justify-between rounded-xl bg-black/30 px-3 py-2">
                        <span className="text-xs font-semibold text-white truncate">{v.venue_name}</span>
                        <span className="shrink-0 ml-2 text-[10px] font-bold uppercase tracking-wide text-emerald-300">
                          {v.visits_at_grant ?? '?'} visits
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: NIGHTLIFE MEMORIES & DIARY */}
            {activeTab === 'memories' && (
              <div className="space-y-3 text-left">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Nightlife Diary & Personal Memories</h4>
                  <button
                    onClick={() => setShowFeedbackModal(true)}
                    className="text-[11px] text-amber-400 font-bold hover:underline flex items-center gap-1"
                  >
                    <Star className="w-3.5 h-3.5" /> Rate Last Visit
                  </button>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {memories.length === 0 && (
                    <div className="text-xs text-slate-500 text-center py-6">
                      No check-ins yet. Check in at a venue to start your diary.
                    </div>
                  )}
                  {memories.map(item => (
                    <div key={item.id} className="p-3 rounded-xl bg-slate-800/70 border border-slate-700/60 flex items-center justify-between text-xs">
                      <div>
                        <div className="font-bold text-white">{item.venueName}</div>
                        <div className="text-[10px] text-slate-400">
                          {item.date ? new Date(item.date).toLocaleDateString() : ''} • {item.type}
                        </div>
                        {item.note && <div className="text-[10px] text-slate-500 mt-0.5">{item.note}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB 3: PREFERENCES */}
            {activeTab === 'preferences' && (
              <div className="space-y-3 text-left">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Profile Details</h4>
                <div className="space-y-2 text-xs">
                  <div className="p-3 rounded-xl bg-slate-800/70 border border-slate-700/60 flex items-center justify-between">
                    <span>City</span>
                    <span className="font-bold text-emerald-400">{prefs.city || 'Not set'}</span>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-800/70 border border-slate-700/60 flex items-center justify-between">
                    <span>Vibe</span>
                    <span className="font-bold text-purple-300">{prefs.vibe || 'Not set'}</span>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-800/70 border border-slate-700/60 flex items-center justify-between">
                    <span>Profession</span>
                    <span className="font-bold text-slate-200">{prefs.profession || 'Not set'}</span>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-800/70 border border-slate-700/60 flex items-center justify-between">
                    <span>Age</span>
                    <span className="font-bold text-amber-300">{prefs.age ?? 'Not set'}</span>
                  </div>
                  {prefs.bio && (
                    <div className="p-3 rounded-xl bg-slate-800/70 border border-slate-700/60">
                      <span className="text-slate-400">Bio</span>
                      <div className="text-slate-200 mt-1">{prefs.bio}</div>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-slate-500">
                  Update these from your profile. Venues only see your public profile details.
                </p>
              </div>
            )}

            {/* PRIVATE POST-VISIT FEEDBACK MODAL */}
            {showFeedbackModal && (
              <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md">
                <div className="w-full max-w-sm bg-slate-900 text-white rounded-2xl p-5 border border-slate-800 space-y-4 text-left shadow-2xl relative">
                  <button onClick={() => setShowFeedbackModal(false)} className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                  <h4 className="text-sm font-bold text-white flex items-center gap-1.5">
                    <Shield className="w-4 h-4 text-emerald-400" /> Private Post-Visit Rating
                  </h4>
                  <p className="text-[11px] text-slate-400">Strictly private feedback to help STAGSTER ops maintain venue quality.</p>

                  <div className="space-y-2 text-xs">
                    <div>
                      <label className="text-slate-300 block mb-1">Music & Vibe (1-5)</label>
                      <select className="w-full bg-slate-950 p-2 rounded-lg border border-slate-800 text-white" value={feedback.musicRating} onChange={(e) => setFeedback({...feedback, musicRating: Number(e.target.value)})}>
                        <option value={5}>5 - Amazing Vibe</option>
                        <option value={4}>4 - Good Music</option>
                        <option value={3}>3 - Average</option>
                        <option value={2}>2 - Below average</option>
                        <option value={1}>1 - Poor</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-slate-300 block mb-1">Door Entry Speed</label>
                      <select className="w-full bg-slate-950 p-2 rounded-lg border border-slate-800 text-white" value={feedback.entrySpeed} onChange={(e) => setFeedback({...feedback, entrySpeed: e.target.value})}>
                        <option value="fast">Fast (Under 5 mins)</option>
                        <option value="normal">Normal Wait</option>
                        <option value="slow">Slow Entry</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-slate-300 block mb-1">Solo Safety & Comfort</label>
                      <select className="w-full bg-slate-950 p-2 rounded-lg border border-slate-800 text-white" value={feedback.safetyRating} onChange={(e) => setFeedback({...feedback, safetyRating: e.target.value})}>
                        <option value="high">High Safety & Respectful Crowd</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low Safety</option>
                      </select>
                    </div>
                  </div>

                  <button onClick={submitFeedback} className="w-full py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 font-bold text-xs transition flex items-center justify-center gap-1.5">
                    <Send className="w-3.5 h-3.5" /> Submit Private Feedback
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
